import { useRef, useState, useEffect } from "react";
import { LIMIT_TEXT_REPORT, MAX_BAN_DAYS } from "../../../utils/vars";
import Spinner from "../../../global-components/src/components/spinner/spiner";
import axios from "axios";
import { useMessage } from "../../../global-components/src";
import { useAuth } from "../../../global-components/src";
import { PACOCA_API_URL } from "../../../global-components/src/utils/vars";
import { logEvent } from "../../analytics-tracker";
import { errorMessage } from "../../../global-components/src/utils/text";
import { User } from "../../../global-components/src/types";
import { useTranslation } from "react-i18next";
import { usePasswordCache } from "../../../hooks/usePasswordCache";

interface BanUserProps {
    user: User | null;
    reasonRequired: boolean;
    onBanned: () => void
}

const BanUser: React.FC<BanUserProps> = ({ user, reasonRequired, onBanned }) => {
    const [text, setText] = useState("");
    const [time, setTime] = useState<number | null>(null);
    const [isLimitExceeded, setIsLimitExceeded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [password, setPassword] = useState("");
    const { savePassword, getPassword, clearPassword } = usePasswordCache();
    const { setMessage } = useMessage();
    const { token } = useAuth();
    const { t } = useTranslation();
    const [errorsInput, setErrorsInput] = useState<any>({});
    const closeRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        const cached = getPassword();
        if (cached) setPassword(cached);
    }, []);

    const limitePost = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = event.target.value;
        setText(newText);
        setIsLimitExceeded(newText.length > LIMIT_TEXT_REPORT);
    }
    const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        setTime(Number(newText)); // Certifique-se de que está atualizando a variável correta
    };
    const handlePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        setPassword(newText); // Certifique-se de que está atualizando a variável correta
    };
    const handleCancelBanUser = () => {
        setText("")
        setTime(null)
        setPassword("")
        clearPassword();
    };

    const handleBanUser = async () => {
        setError("")
        setErrorsInput("");
        setIsLoading(true)
        const url = PACOCA_API_URL + `/users/ban/${user?.id}`;

        try {
            let errors: Record<string, string[]> = {};
            if (!text) {
                errors.reason = [t("moderation.ban.reasonRequired")];
            }
            if (text.length <= 2 || text.toLowerCase() == "regras" || text.toLowerCase() == "regra") {
                errors.reason = [t("errors.input.minLength", { field: t("generics.message"), min: 3 })]
            }
            if (!password) {
                errors.password = [t("errors.input.required", { field: t("inputs.password") })]
            }
            if (time && time <= 0) {
                errors.time = [t("moderation.ban.daysRequired")];
            }
            if (time && time > MAX_BAN_DAYS) {
                errors.time = [t("moderation.ban.maxDays", { max: MAX_BAN_DAYS })];
            }
            // if (!time) {
            //     errors.time = ["A quantidade de dias é obrigatória."];
            // }

            if (Object.keys(errors).length > 0) {
                throw {
                    response: {
                        data: {
                            errors
                        }
                    }
                };
            }

            logEvent("novo_banimento", {
                categoria: "Banimento",
                acao: "Novo Banimento",
            });

            await axios.post(url, { time, password, id_user: user?.id, reason: text }, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            savePassword(password);

            if (closeRef.current)
                closeRef.current.click()

            setText("")
            setTime(null)
            setPassword("")
            setMessage(t("moderation.ban.success"), "success");
            onBanned()
        } catch (err: any) {
            if (err?.response?.data.errors) {
                setErrorsInput(err.response.data.errors); // Atualiza o estado com os erros
            } else {
                const messageError = errorMessage(err);
                setError(messageError);
                console.error("Erro banir usuário: ", messageError);
                console.error("Erro banir usuário: ", err);
            }
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="modal fade" id="modal-ban" tabIndex={-1} aria-labelledby="modal-ban" aria-hidden="true">
            <div className="modal-dialog cover-mobile">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title" id="modal-ban">{t("moderation.ban.title", { name: user?.name.split(" ")[0] })}</h5>
                        <button ref={closeRef} type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t("moderation.ban.close")}></button>
                    </div>
                    <div className="modal-body">
                        <p>{t("moderation.ban.confirmation", { name: user?.name.split(" ")[0] })}</p>

                        <label htmlFor="time" className="">{t("moderation.ban.durationHint")}</label>
                        <input value={time ?? ""} onChange={handleInput} required className={`form-control ${errorsInput.time ? 'is-invalid' : ''}`} max="10" type="number" name="time" id="time" />
                        {errorsInput.time && (
                            <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.time[0]}</div> // Exibe a mensagem de erro
                        )}

                        <label htmlFor="time" className="mt-3 label-required">{t("inputs.password")}</label>
                        <input name="username" autoComplete="username" className="d-none"/> {/* Campo oculto para evitar remover warning */}
                        <input value={password ?? ""} onChange={handlePassword} required className={`form-control ${errorsInput.password ? 'is-invalid' : ''}`} max="10" type="password" />
                        {errorsInput.password && (
                            <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.password[0]}</div> // Exibe a mensagem de erro
                        )}

                        <label htmlFor="reason" className={`mt-3 ${reasonRequired ? "label-required" : ""}`}>{t("moderation.ban.reason")}</label>
                        <textarea style={{ color: isLimitExceeded ? "red" : "inherit" }} onChange={(e) => limitePost(e)} value={text} className={`${errorsInput.reason ? 'is-invalid' : ''} form-control`} name="reason" id="reason" required={reasonRequired} cols={30} rows={10}></textarea>
                        {errorsInput.reason && (
                            <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.reason[0]}</div> // Exibe a mensagem de erro
                        )}

                        <div className="user-select-none limite-denuncia">
                            {text.length}/{LIMIT_TEXT_REPORT}
                        </div>

                        <div className="invalid-feedback invalid-feedback-comment-text" style={{ display: "block" }}>
                            {error}
                        </div>
                        <div className="modal-footer modal-footer-modal">
                            <button type="button" onClick={handleCancelBanUser} className="btn btn-secondary btn-modal" data-bs-dismiss="modal">{t("generics.cancel")}</button>
                            <button disabled={isLimitExceeded} onClick={handleBanUser} type="submit" className="btn btn-blue btn-modal">
                                {!isLoading ? (
                                    <>{t("moderation.ban.action")}</>
                                ) : (
                                    <Spinner />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BanUser;
